import { useCallback, useEffect, useRef, useState } from 'react';
import { breakpoints, Inline, LinkButton, Stack, Text, ValidationMessage } from '@etg/wings';
import { AgeType, Tier } from '@eti/schema-types';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { ContentWrapper } from '@eti/components';
import { css } from '@eti/styles';
import { analytics } from '@eti/utils';
import { getIncludedProductPrice, saveUpToAmountWithBundle, uniqueFeatures } from '../utils/utils';
import EtgFaresSelection from './EtgFaresSelection';
import EtgFaresHeading from './EtgFaresHeading';
import EtgFaresDrawer from './EtgFaresDrawer';
import EtgFaresStickyFooter from './EtgFaresStickyFooter';
import EtgFaresPersuasionModal from './EtgFaresPersuasionModal';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const growContainerStyles = headerAndStepIndicatorHeight => css`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - ${headerAndStepIndicatorHeight}px);
`;
const sectionWrapperStyles = css`
  margin-bottom: 24px;
  margin-top: 10px;

  @media (min-width: ${breakpoints._768}) {
    margin-top: 24px;
  }
`;
export const linkButtonStyles = css`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  list-style: none;

  li {
    display: inline;
  }
`;
const stepIndicatorHeight = 45;
const EtgFares = ({
  availableExtraProducts,
  bounds,
  cheapestPaymentMethods,
  handleProceedEtgFareClick,
  discountAmount,
  discountPercentage,
  isSelectTripLoading,
  name,
  options,
  otherPaymentMethods,
  paymentMethodPrices,
  PriceInformationModal,
  selectedExtraProducts,
  selectedEtgFareOption,
  selectionKey,
  setSelectedEtgFareOption,
  travelerPrices,
  travelerPricesWithoutDiscounts,
  tripTravelers,
  tripType
}) => {
  var _travelerPrices$find;
  const {
    t
  } = useTranslation();
  const {
    p
  } = useProperty();
  const isPersuasionModalEnabled = p('ETG.Fares.PersuasionModal.Enabled');
  const {
    headerAndInfoHeight
  } = useSiteContext();
  const headerAndStepIndicatorHeight = headerAndInfoHeight + stepIndicatorHeight;
  const [hasValidationError, setHasValidationError] = useState(false);
  const [shouldProceedAfterModalSelection, setShouldProceedAfterModalSelection] = useState(false);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isPriceModalOpen, setPriceModalOpen] = useState(false);
  const [isPersuasionModalOpen, setPersuasionModalOpen] = useState(false);
  const handleDrawer = () => {
    setDrawerOpen(prevIsOpen => !prevIsOpen);
  };
  const handlePriceModal = () => {
    setPriceModalOpen(prevIsOpen => !prevIsOpen);
  };
  const handlePersuasionModal = () => {
    setPersuasionModalOpen(prevIsOpen => !prevIsOpen);
  };
  const firstRadioRef = useRef(null);
  const validationRef = useRef(null);
  const etgFaresDetailsDrawerLinkText = t('Product.ETG.Fares.Details.Drawer.Link');
  analytics.useLogMouseFlowTagEffect('ETG bundle');
  const {
    id: selectedEtgFareId
  } = selectedEtgFareOption !== null && selectedEtgFareOption !== void 0 ? selectedEtgFareOption : {};
  const sellableOptions = options.filter(option => option.type !== Tier.Basic);
  const basicEtgFareOption = options.find(option => option.type === Tier.Basic);
  const hasDiscount = discountPercentage ? discountPercentage > 0 : false;
  const travelersWithoutInfants = tripTravelers.filter(traveler => traveler.ageType !== AgeType.Inf);
  const numberOfTravelersWithoutInfants = travelersWithoutInfants.length;
  const tripPricePerAdult = ((_travelerPrices$find = travelerPrices.find(travelerPrice => travelersWithoutInfants.some(traveler => traveler.ageType === AgeType.Adt && traveler.id === travelerPrice.travelerId))) === null || _travelerPrices$find === void 0 || (_travelerPrices$find = _travelerPrices$find.price) === null || _travelerPrices$find === void 0 || (_travelerPrices$find = _travelerPrices$find.price) === null || _travelerPrices$find === void 0 ? void 0 : _travelerPrices$find.value) || 0;
  const includedInTripProductPrice = getIncludedProductPrice(availableExtraProducts, selectedExtraProducts);
  const totalTripPrice = travelerPrices.reduce((acc, total) => acc + total.price.price.value, 0);
  const handleProceedButtonClick = useCallback(option => {
    handleProceedEtgFareClick(selectedExtraProducts, option === null || option === void 0 ? void 0 : option.id);
  }, [handleProceedEtgFareClick, selectedExtraProducts]);
  const handleOnChange = event => {
    const selectedOptionId = event.target.value;
    const selectedOption = options.find(option => (option === null || option === void 0 ? void 0 : option.id) === selectedOptionId);
    if (hasValidationError) {
      setHasValidationError(false);
    }
    if (selectedOption) {
      setSelectedEtgFareOption(selectedOption);
    }
  };
  const hasPersuasionModal = isPersuasionModalEnabled && sessionStorage.getItem('persuasionModalSelectionKey') !== selectionKey;
  const handleSkipStep = () => {
    if (hasPersuasionModal) {
      sessionStorage.setItem('persuasionModalSelectionKey', selectionKey);
      handlePersuasionModal();
    } else {
      handleProceedButtonClick(basicEtgFareOption);
    }
  };
  const handleModalOptionSelect = option => {
    if (hasValidationError) {
      setHasValidationError(false);
    }
    setSelectedEtgFareOption(option);
    setShouldProceedAfterModalSelection(true);
  };
  useEffect(() => {
    if (!shouldProceedAfterModalSelection || !selectedEtgFareId || !isPersuasionModalOpen) {
      return;
    }
    if (isPersuasionModalOpen) {
      handleProceedButtonClick(selectedEtgFareOption);
      setShouldProceedAfterModalSelection(false);
    }
  }, [handleProceedButtonClick, isPersuasionModalOpen, selectedEtgFareId, shouldProceedAfterModalSelection, selectedEtgFareOption]);
  useEffect(() => {
    if (hasValidationError && validationRef.current) {
      var _validationRef$curren;
      (_validationRef$curren = validationRef.current) === null || _validationRef$curren === void 0 || _validationRef$curren.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      setTimeout(() => {
        var _validationRef$curren2;
        (_validationRef$curren2 = validationRef.current) === null || _validationRef$curren2 === void 0 || _validationRef$curren2.focus();
      }, 400);
    }
  }, [hasValidationError]);
  const onProceedClick = () => {
    if (!selectedEtgFareId) {
      setHasValidationError(true);
    } else {
      handleProceedButtonClick(selectedEtgFareOption);
    }
  };
  return /*#__PURE__*/_jsxs("section", {
    className: growContainerStyles(headerAndStepIndicatorHeight),
    "data-testid": "etg-fares",
    children: [/*#__PURE__*/_jsx(ContentWrapper, {
      className: sectionWrapperStyles,
      children: /*#__PURE__*/_jsxs(Stack, {
        spacing: 24,
        children: [/*#__PURE__*/_jsx(EtgFaresHeading, {
          discountPercentage: discountPercentage !== null && discountPercentage !== void 0 ? discountPercentage : 0,
          hasDiscount: hasDiscount,
          numberOfSellableOptions: sellableOptions.length,
          savedAmount: saveUpToAmountWithBundle(sellableOptions, discountPercentage !== null && discountPercentage !== void 0 ? discountPercentage : 0)
        }), /*#__PURE__*/_jsx(EtgFaresDrawer, {
          bounds: bounds,
          dataTestId: "etg-overlay-drawer",
          handleClose: handleDrawer,
          isOpen: isDrawerOpen,
          sellableOptions: uniqueFeatures(sellableOptions),
          title: etgFaresDetailsDrawerLinkText,
          tripType: tripType
        }), hasValidationError && /*#__PURE__*/_jsx(ValidationMessage, {
          "aria-labelledby": "etg-fares-validation-message-text",
          "data-testid": "etg-fares-validation-message",
          ref: validationRef,
          tabIndex: -1,
          variant: "error",
          children: /*#__PURE__*/_jsxs(Inline, {
            spacing: 8,
            children: [/*#__PURE__*/_jsx(Text, {
              "aria-live": "assertive",
              id: "etg-fares-validation-message-text",
              children: t('Product.ETG.Fares.Validation.Message')
            }), /*#__PURE__*/_jsxs("ul", {
              className: linkButtonStyles,
              children: [/*#__PURE__*/_jsx("li", {
                children: /*#__PURE__*/_jsx(LinkButton, {
                  onClick: () => {
                    var _firstRadioRef$curren;
                    return (_firstRadioRef$curren = firstRadioRef.current) === null || _firstRadioRef$curren === void 0 ? void 0 : _firstRadioRef$curren.focus();
                  },
                  children: t('Product.ETG.Fares.Validation.SelectOption.Text')
                })
              }), /*#__PURE__*/_jsx("li", {
                children: /*#__PURE__*/_jsx(LinkButton, {
                  onClick: () => handleProceedButtonClick(basicEtgFareOption),
                  children: t('YourSelectedTrip.Page.SkipStepButton.Label')
                })
              })]
            })]
          })
        }), /*#__PURE__*/_jsx(EtgFaresSelection, {
          bounds: bounds,
          discountPercentage: discountPercentage !== null && discountPercentage !== void 0 ? discountPercentage : 0,
          firstRadioRef: firstRadioRef,
          handleOnChange: handleOnChange,
          hasDiscount: hasDiscount,
          hasValidationError: hasValidationError,
          name: name,
          numberOfTravelersWithoutInfants: numberOfTravelersWithoutInfants,
          sellableOptions: sellableOptions
        }), /*#__PURE__*/_jsx(Inline, {
          children: /*#__PURE__*/_jsx(LinkButton, {
            "aria-controls": "product-etg-info-drawer",
            "aria-expanded": isDrawerOpen ? 'true' : 'false',
            "aria-haspopup": "dialog",
            "data-testid": `${name}-drawer-link-content`,
            onClick: handleDrawer,
            children: etgFaresDetailsDrawerLinkText
          })
        })]
      })
    }), /*#__PURE__*/_jsx(EtgFaresStickyFooter, {
      handlePriceModalOpen: handlePriceModal,
      hasPersuasionModal: hasPersuasionModal,
      includedInTripProductPrice: includedInTripProductPrice,
      isSelectTripLoading: isSelectTripLoading,
      numberOfTravelersWithoutInfants: numberOfTravelersWithoutInfants,
      onProceedClick: onProceedClick,
      onSkipStepClick: handleSkipStep,
      selectedBundlePrice: (selectedEtgFareOption === null || selectedEtgFareOption === void 0 ? void 0 : selectedEtgFareOption.priceRaw) || 0,
      totalTripPrice: totalTripPrice,
      tripPricePerAdult: tripPricePerAdult
    }), /*#__PURE__*/_jsx(PriceInformationModal, {
      availableExtraProducts: availableExtraProducts,
      bounds: bounds,
      cheapestPaymentMethods: cheapestPaymentMethods,
      discountAmount: discountAmount !== null && discountAmount !== void 0 ? discountAmount : 0,
      finalProductIdsToPurchase: selectedExtraProducts,
      handleClose: handlePriceModal,
      isOpen: isPriceModalOpen,
      otherPaymentMethods: otherPaymentMethods,
      paymentMethodPrices: paymentMethodPrices,
      selectedBundleOption: selectedEtgFareOption,
      travelerPrices: travelerPrices,
      travelerPricesWithoutDiscounts: travelerPricesWithoutDiscounts,
      tripTravelers: tripTravelers
    }), /*#__PURE__*/_jsx(EtgFaresPersuasionModal, {
      bounds: bounds,
      dataTestId: "etg-fares-persuasion-modal-dialog",
      handleClosePersuasionModal: handlePersuasionModal,
      handleModalOptionSelect: handleModalOptionSelect,
      handleSkipStep: handleSkipStep,
      hasDiscount: hasDiscount,
      isPersuasionModalOpen: isPersuasionModalOpen,
      numberOfTravelersWithoutInfants: numberOfTravelersWithoutInfants,
      sellableOptions: sellableOptions
    })]
  });
};
export default EtgFares;
//# sourceMappingURL=EtgFares.js.map